// 3-column scroll-snap wheel picker, iOS-style. Used by MealReview to let
// the user pick a quantity (integer + optional fraction + unit) for a meal
// item without typing free text.
//
// Sync model:
//   - The Column component owns its scroll position.
//   - It mirrors `column.value` to scroll position ONLY when value actually
//     changes (deep compare via Object.is) — not on every parent re-render
//     (which caused the runaway feedback loops in the previous version).
//   - It fires `column.onChange` ONLY when the user finishes scrolling
//     (scrollend event with debounce fallback) — not on every scroll tick.

import { useEffect, useRef } from "react";

export interface WheelColumn<T extends string | number> {
  /** Stable id, used for keys + accessibility labels. */
  key: string;
  /** Ordered list of selectable values. */
  values: T[];
  /** Currently-selected value. */
  value: T;
  /** Fired when the user scrolls to a new value. */
  onChange: (next: T) => void;
  /** Optional renderer — defaults to String(value). */
  render?: (value: T) => string;
  /** Width hint in tailwind units (e.g. "w-16"). Defaults to "flex-1". */
  width?: string;
  /** Hide this column entirely — for the fraction column when unit is mass. */
  hidden?: boolean;
  /** Aria label for the column, e.g. "Quantity", "Unit". */
  ariaLabel?: string;
}

interface WheelPickerProps {
  columns: WheelColumn<string | number>[];
  /** Height of one row in pixels. Default 44 (iOS standard tap target). */
  rowHeightPx?: number;
  /** Number of rows visible above + below the center. Default 2 → 5 rows total. */
  visibleAboveBelow?: number;
}

export default function WheelPicker({
  columns,
  rowHeightPx = 44,
  visibleAboveBelow = 2
}: WheelPickerProps) {
  const totalRows = 1 + visibleAboveBelow * 2;
  const containerHeight = totalRows * rowHeightPx;

  return (
    <div
      className="relative rounded-xl bg-slate-800/60 border border-slate-700/60 overflow-hidden flex"
      style={{ height: containerHeight }}
      role="group"
    >
      <div
        className="absolute left-0 right-0 pointer-events-none border-y border-slate-600/60 bg-slate-700/20"
        style={{ top: visibleAboveBelow * rowHeightPx, height: rowHeightPx }}
      />
      <div
        className="absolute left-0 right-0 top-0 pointer-events-none z-10 bg-gradient-to-b from-slate-800/90 to-transparent"
        style={{ height: visibleAboveBelow * rowHeightPx }}
      />
      <div
        className="absolute left-0 right-0 bottom-0 pointer-events-none z-10 bg-gradient-to-t from-slate-800/90 to-transparent"
        style={{ height: visibleAboveBelow * rowHeightPx }}
      />

      {columns
        .filter((c) => !c.hidden)
        .map((col) => (
          <Column
            key={col.key}
            column={col}
            rowHeightPx={rowHeightPx}
            visibleAboveBelow={visibleAboveBelow}
          />
        ))}
    </div>
  );
}

function Column({
  column,
  rowHeightPx,
  visibleAboveBelow
}: {
  column: WheelColumn<string | number>;
  rowHeightPx: number;
  visibleAboveBelow: number;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // The last value WE pushed to onChange — used to suppress echo loops
  // (parent updates value → we'd otherwise fire onChange again).
  const lastEmittedRef = useRef<string | number>(column.value);
  // The last `column.value` we synced our scroll position to — lets us
  // avoid spurious scrollTo calls when the parent re-renders without
  // actually changing the value.
  const lastSyncedValueRef = useRef<string | number | null>(null);
  // Debounce timer for scrollend fallback on browsers that don't support it.
  const scrollEndTimerRef = useRef<number | null>(null);

  // Sync scroll position to the externally-set value. Only fires when
  // column.value ACTUALLY changes (not when values array reference changes).
  useEffect(() => {
    if (lastSyncedValueRef.current === column.value) return;
    lastSyncedValueRef.current = column.value;
    lastEmittedRef.current = column.value;

    const i = column.values.indexOf(column.value);
    if (i < 0) return;
    const el = scrollRef.current;
    if (!el) return;
    const targetScrollTop = i * rowHeightPx;
    // Only scroll if not already there (cheap optimization but also
    // suppresses redundant scrollend events).
    if (Math.abs(el.scrollTop - targetScrollTop) > 1) {
      el.scrollTo({ top: targetScrollTop, behavior: "auto" });
    }
  }, [column.value, column.values, rowHeightPx]);

  function commitFromScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const i = Math.round(el.scrollTop / rowHeightPx);
    const clamped = Math.max(0, Math.min(column.values.length - 1, i));
    const v = column.values[clamped];
    // Snap visually to the rounded position (in case scrollend left us mid-row)
    const snappedTop = clamped * rowHeightPx;
    if (Math.abs(el.scrollTop - snappedTop) > 1) {
      el.scrollTo({ top: snappedTop, behavior: "auto" });
    }
    // Only fire onChange if the value actually changed from what we last
    // emitted — prevents feedback loops when the parent re-pushes the same
    // value during a render.
    if (v !== lastEmittedRef.current) {
      lastEmittedRef.current = v;
      column.onChange(v);
    }
  }

  // Listen for scrollend (modern browsers). Fallback: debounce onScroll.
  // We use ref-based handlers (not React's onScroll prop) so we can attach
  // scrollend, which React doesn't support directly.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    function handleScroll() {
      // Reset the debounce timer on every scroll tick.
      if (scrollEndTimerRef.current != null) {
        window.clearTimeout(scrollEndTimerRef.current);
      }
      scrollEndTimerRef.current = window.setTimeout(() => {
        commitFromScroll();
      }, 120);
    }

    function handleScrollEnd() {
      if (scrollEndTimerRef.current != null) {
        window.clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = null;
      }
      commitFromScroll();
    }

    el.addEventListener("scroll", handleScroll, { passive: true });
    // scrollend is supported in iOS 17+ / Chrome 114+ — falls through to
    // the 120ms scroll-stop debounce on older devices.
    el.addEventListener("scrollend", handleScrollEnd as EventListener);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      el.removeEventListener("scrollend", handleScrollEnd as EventListener);
      if (scrollEndTimerRef.current != null) {
        window.clearTimeout(scrollEndTimerRef.current);
      }
    };
    // commitFromScroll is recreated each render but closes over latest column
    // via refs, so deps are intentionally limited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column.values, column.onChange, rowHeightPx]);

  // Active index for highlighting — derive from props, no internal state.
  const activeIdx = column.values.indexOf(column.value);
  const spacers = Array.from({ length: visibleAboveBelow });
  const render = column.render ?? ((v: string | number) => String(v));

  return (
    <div className={`relative ${column.width ?? "flex-1"} h-full`}>
      <div
        ref={scrollRef}
        className="h-full overflow-y-scroll snap-y snap-mandatory no-scrollbar"
        style={{ scrollbarWidth: "none" }}
        aria-label={column.ariaLabel ?? "Picker column"}
      >
        {spacers.map((_, i) => (
          <div key={`sp-top-${i}`} style={{ height: rowHeightPx }} />
        ))}
        {column.values.map((v, i) => (
          <div
            key={`${column.key}-${i}`}
            className={`snap-center flex items-center justify-center tabular-nums select-none ${
              i === activeIdx ? "text-slate-100 font-semibold" : "text-slate-500"
            }`}
            style={{ height: rowHeightPx }}
            aria-current={i === activeIdx ? "true" : undefined}
          >
            {render(v)}
          </div>
        ))}
        {spacers.map((_, i) => (
          <div key={`sp-bot-${i}`} style={{ height: rowHeightPx }} />
        ))}
      </div>
    </div>
  );
}
